import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@common/api/authService/supabase';
import { PulseReport } from '@common/api/featureService';
import Skeleton from '@common/components/Skeleton';
import { Button, Icon } from '@common/index';
import {
    CalendarBlank
} from '@phosphor-icons/react';
import { snapdom } from '@zumer/snapdom';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import {
    ChevronLeft,
    Download
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WebPage } from '../WebPage.tsx';
import { ArtifactMarkdown } from './ArtifactMarkdown.tsx';
import styles from './index.module.scss';

export const PulseDetail = () => {
    const navigate = useNavigate();
    const { pulseId } = useParams();
    const [pulseDetail, setPulseDetail] = useState<PulseReport | null>(null);
    const [loading, setLoading] = useState(true);
    const { modal } = useAdminContext();
    const {pulse, chat} = useAdminContext();
    const { setContext } = chat;
    const { currentWorkspace } = useAuth();

    // Format date to display format like "Nov 07, 2025"
    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return dayjs(dateString).format('MMM DD, YYYY');
    };

    // Download markdown file directly from OSS
    const downloadMarkdown = async () => {
        if (!pulseDetail?.oss_url) return;
        
        try {
            const response = await fetch(pulseDetail.oss_url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${pulseDetail.title || 'pulse'}.md`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('下载 markdown 文件失败:', error);
        }
    };

    // Convert current rendered content to PDF using snapdom
    const downloadPDF = async () => {
        if (!pulseDetail?.oss_url) return;
        
        try {
            // Find the pulse content element that's already rendered in the browser
            const pulseContentElement = document.querySelector(`.${styles.pulseContent}`);
            if (!pulseContentElement) {
                console.error('找不到内容区域');
                return;
            }
            
            // Clone the element to ensure we capture the full content without scroll limitations
            const clonedElement = pulseContentElement.cloneNode(true) as HTMLElement;
            
            // Create a temporary container
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.top = '-9999px';
            container.style.left = '-9999px';
            container.style.width = `${pulseContentElement.clientWidth}px`; // Keep original width
            container.style.zIndex = '-1';
            
            // Reset styles on the cloned element to ensure full height visibility
            clonedElement.style.height = 'auto';
            clonedElement.style.overflow = 'visible';
            clonedElement.style.maxHeight = 'none';
            clonedElement.style.borderRadius = '0';
            
            container.appendChild(clonedElement);
            document.body.appendChild(container);
            
            // Use snapdom to capture the cloned content
            const result = await snapdom(clonedElement, {
                scale: 2,
                backgroundColor: '#ffffff',
                embedFonts: true,
                useCORS: true,
                allowTaint: true,
            } as any);
            
            // Clean up
            document.body.removeChild(container);
            
            // Get canvas from snapdom result
            const canvas = await result.toCanvas();
            
            // Get image data from canvas
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            // Set PDF width to A4 width (210mm) and calculate height based on aspect ratio
            const pdfWidth = 210;
            const pdfHeight = (imgHeight * pdfWidth) / imgWidth;

            const pdf = new jsPDF({
                orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
                unit: 'mm',
                format: [pdfWidth, pdfHeight]
            });

            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            
            pdf.save(`${pulseDetail.title || 'pulse'}.pdf`);
        } catch (error) {
            console.error('生成 PDF 失败:', error);
        }
    };

    // 如果 pulseData 为空，尝试获取数据
    useEffect(() => {
        if (pulseId) {
            const _pulseDetail = pulse.pulseData?.find((item) => item.id === pulseId);
            if(_pulseDetail){
                setPulseDetail(_pulseDetail);
                return;
            }
            // 获取所有 pulse 数据
            const fetchPulseData = async () => {
                try {
                    setLoading(true);
                    const workspaceId = currentWorkspace?.id;
                    if (!workspaceId) {
                        console.error('Workspace ID not found');
                        setPulseDetail(null);
                        return;
                    }

                    const { data, error } = await supabase
                        .from('workspace_pulse_report')
                        .select('*')
                        .eq('id', pulseId)
                        .eq('workspace_id', workspaceId)
                        .single();

                    if (error) {
                        console.error('Failed to fetch pulse detail:', error);
                        setPulseDetail(null);
                        return;
                    }

                    // 映射数据到 PulseReport 格式
                    const report: PulseReport = {
                        id: data.id,
                        user_id: data.workspace_id,
                        report_date: data.created_at,
                        report_type: data.report_type,
                        title: data.title,
                        brief: data.brief,
                        img_url: data.img_url,
                        oss_url: data.oss_url,
                        status: data.status,
                        created_at: data.created_at,
                        updated_at: data.updated_at
                    };

                    setPulseDetail(report);
                } catch (err) {
                    console.error('Failed to fetch pulse reports:', err);
                    setPulseDetail(null);
                } finally {
                    setLoading(false);
                }
            };
            fetchPulseData();
        }
    }, [pulseId, currentWorkspace?.id, pulse.pulseData]);

    useEffect(() => {
        if(pulseDetail) {
            setContext([{
                payload: {pulseId: pulseDetail?.id || '',},
                title: pulseDetail?.title || '',
                type: 'file',
            }]);
        }
        return () => {
            setContext([]);
        }
    }, [pulseDetail]);

    const handleBack = () => {
        navigate('/pulse');
    };

    if(!loading && !pulseDetail?.oss_url){
        return (
            <div className={styles.notFound}>
                <Icon name="lock" size={48} color="#1A162F" />
                <h2>You don't have access to this Pulse</h2>
                <Button size="2" onClick={() => navigate('/')}>Back to home</Button>
            </div>
        )
    }
    
    const isHtml = pulseDetail?.oss_url?.endsWith('.html');

    return (
        <div className={styles.layout}>
            <div className={`${styles.pulseDetail}`}>
                <div className={styles.container}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <div className={styles.headerMeta}>
                                <button
                                    className={styles.backButton}
                                    onClick={handleBack}
                                    type="button"
                                >
                                    <ChevronLeft size={18} className={styles.backIcon} />
                                    <span>Back</span>
                                </button>

                                <span className={styles.metaItem}>
                                    <CalendarBlank size={14} weight="bold" className={styles.metaIcon} />
                                    {formatDate(pulseDetail?.report_date || pulseDetail?.created_at || '')}
                                </span>
                            </div>

                            <h1 className={styles.title}>
                                {pulseDetail?.title || ''}
                            </h1>
                        </div>

                        <div className={styles.headerRight}>
                            {/* <button className={styles.shareButton}>
                                <Share2 size={18} />
                                Share
                            </button> */}
                            <Button 
                                className={styles.downloadButton}
                                onClick={isHtml ? downloadPDF : downloadMarkdown}
                            >
                                <Download size={18} />
                                Download 
                                {' '}
                                {isHtml ? 'PDF' : 'Markdown'}
                            </Button>
                        </div>
                    </div>

                    <div className={styles.pulseContent}>
                        {loading ? (
                            <div className={styles.skeletonWrapper}>
                                <Skeleton width="100%" height="24px" marginBottom="16px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="90%" height="16px" marginBottom="12px" />
                                <Skeleton width="95%" height="16px" marginBottom="12px" />
                                <Skeleton width="85%" height="16px" marginBottom="12px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="92%" height="16px" marginBottom="12px" />
                                <Skeleton width="88%" height="16px" marginBottom="12px" />
                                <Skeleton width="96%" height="16px" marginBottom="12px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="90%" height="16px" marginBottom="16px" />
                                <Skeleton width="60%" height="400px" marginBottom="16px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                                <Skeleton width="95%" height="16px" marginBottom="12px" />
                                <Skeleton width="100%" height="16px" marginBottom="12px" />
                            </div>
                        ) : null}
                        {isHtml ? (
                            <WebPage html={pulseDetail?.oss_url || ''} onLoad={() => setLoading(false)} />
                        ) : (
                            <ArtifactMarkdown url={pulseDetail?.oss_url || ''} onLoad={() => setLoading(false)} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
